import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';
import { PangolinNetworkThroughput } from 'ui/devices/network_throughput/network_throughput';

import { BasePath } from 'services/base/page_path';

import { Pangolin, User } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { countConnectedDevices } from 'services/pangolin/hosts_service';

import { Redirect } from 'pages/core/redirect/redirect';

import { themedModuleIconClassStyles } from 'assets/enums/modules/modules';

import {
  getLocalNameByCode,
  getCountryByDialCode,
} from 'services/base/countries';

import baseStyles from './device_information.module.css';
import Messages from './device_information_messages';

type DeviceInformationStyles = {
  bandwidthHeaderContainer: string;
  secondaryInfoLabel: string;
  secondaryInfoValue: string;
  bandwidthInfoContainer: string;
  bandwidthHeaderLabel: string;
  downloadValueText: string;
  bandwidthUnitText: string;
  uploadValueText: string;
  secondaryInfoContainer: string;
  secondaryInfoRow: string;
  throughputContainer: string;
  linksContainer: string;
  linksRow: string;
  linkLabel: string;
  row: string;
  deviceNameContainer: string;
  link: string;
  icon: string;
};

const themedStyles: ThemedStyles<DeviceInformationStyles> = {
  [Theme.DAYLIGHT]: {
    bandwidthHeaderContainer: baseStyles.daylightBandwidthHeader,
    secondaryInfoLabel: baseStyles.daylightSecondaryInfoLabel,
    secondaryInfoValue: baseStyles.daylightSecondaryInfoValue,
    bandwidthInfoContainer: baseStyles.bandwidthInfoContainer,
    bandwidthHeaderLabel: baseStyles.bandwidthHeaderLabel,
    downloadValueText: baseStyles.downloadValueText,
    bandwidthUnitText: baseStyles.bandwidthUnitText,
    uploadValueText: baseStyles.uploadValueText,
    secondaryInfoContainer: baseStyles.secondaryInfoContainer,
    secondaryInfoRow: baseStyles.secondaryInfoRow,
    throughputContainer: baseStyles.throughputContainer,
    linksContainer: baseStyles.linksContainer,
    linksRow: baseStyles.linksRow,
    linkLabel: baseStyles.linkLabel,
    row: baseStyles.row,
    deviceNameContainer: baseStyles.deviceNameContainer,
    link: baseStyles.daylightLink,
    icon: baseStyles.icon,
  },
  [Theme.MIDNIGHT]: {
    bandwidthHeaderContainer: baseStyles.midnightBandwidthHeader,
    secondaryInfoLabel: baseStyles.midnightSecondaryInfoLabel,
    secondaryInfoValue: baseStyles.midnightSecondaryInfoValue,
    bandwidthInfoContainer: baseStyles.bandwidthInfoContainer,
    bandwidthHeaderLabel: baseStyles.bandwidthHeaderLabel,
    downloadValueText: baseStyles.downloadValueText,
    bandwidthUnitText: baseStyles.bandwidthUnitText,
    uploadValueText: baseStyles.uploadValueText,
    secondaryInfoContainer: baseStyles.secondaryInfoContainer,
    secondaryInfoRow: baseStyles.secondaryInfoRow,
    throughputContainer: baseStyles.throughputContainer,
    linksContainer: baseStyles.linksContainer,
    linksRow: baseStyles.linksRow,
    linkLabel: baseStyles.linkLabel,
    row: baseStyles.row,
    deviceNameContainer: baseStyles.deviceNameContainer,
    link: baseStyles.midnightLink,
    icon: baseStyles.icon,
  },
};

type DownloadDetailsProps = {
  pangolin: Pangolin;
};

const DownloadDetails = ({ pangolin }: DownloadDetailsProps) => {
  const styles = useStyles(themedStyles);
  if (pangolin.bandwidthDownloadString != null && pangolin.bandwidthDownloadString.trim().length > 0) {
    return (
      <>
        <span className={styles.downloadValueText}>
          {pangolin.bandwidthDownloadString}
        </span>
        <span className={styles.bandwidthUnitText}>
          {pangolin.bandwidthDownloadUnits}
        </span>
      </>
    );
  } else {
    return (
      <span>-</span>
    );
  }
};

type UploadDetailsProps = {
  pangolin: Pangolin;
};

const UploadDetails = ({ pangolin }: UploadDetailsProps) => {
  const styles = useStyles(themedStyles);
  if (pangolin.bandwidthUploadString != null && pangolin.bandwidthUploadString.trim().length > 0) {
    return (
      <>
        <span className={styles.uploadValueText}>
          {pangolin.bandwidthUploadString}
        </span>
        <span className={styles.bandwidthUnitText}>
          {pangolin.bandwidthUploadUnits}
        </span>
      </>
    );
  } else {
    return (
      <span>-</span>
    );
  }
};

type PrimaryInfoProps = {
  pangolin: Pangolin;
};

const PrimaryInfo = ({ pangolin }: PrimaryInfoProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.bandwidthInfoContainer}>
      <div className={styles.bandwidthHeaderContainer}>
        <div className={styles.bandwidthHeaderLabel}>{Messages.downloadLabel()}</div>
        <DownloadDetails pangolin={pangolin}/>
      </div>
      <div className={styles.bandwidthHeaderContainer}>
        <div className={styles.bandwidthHeaderLabel}>{Messages.uploadLabel()}</div>
        <UploadDetails pangolin={pangolin}/>
      </div>
    </div>
  );
};

type DevicesConnectedProps = {
  deviceCount: number;
};

const DevicesConnected = ({ deviceCount }: DevicesConnectedProps) => {
  const styles = useStyles(themedStyles);
  if (deviceCount != null) {
    return (
      <div className={styles.secondaryInfoValue}>
        {deviceCount <= 99 ?
          Messages.connectedDevices(deviceCount) :
          Messages.connectedDevicesOver99()}
      </div>
    );
  } else {
    return (
      <div className={styles.secondaryInfoValue}>-</div>
    );
  }
};

function getUserLocation(user: User): string {
  const country = getCountryByDialCode(user.mobileNumberPrefix, user.country);
  return country ? getLocalNameByCode(country.code) ?? country.baseName : user.country ?? '-';
}

type SecondaryInfoProps = {
  user: User;
  domain: IPangolinDomain;
};

const SecondaryInfo = ({ user, domain }: SecondaryInfoProps) => {
  const styles = useStyles(themedStyles);

  return (
    <div className={styles.secondaryInfoContainer}>
      <div className={styles.secondaryInfoRow}>
        <div className={styles.secondaryInfoLabel}>
          {Messages.devicesLabel()}
        </div>
        <DevicesConnected deviceCount={countConnectedDevices(domain.hosts)}/>
      </div>
      <div className={styles.secondaryInfoRow}>
        <div className={styles.secondaryInfoLabel}>
          {Messages.userLocationLabel()}
        </div>
        <div className={styles.secondaryInfoValue}>
          {getUserLocation(user)}
        </div>
      </div>
    </div>
  );
};

const Links = ({ pangolin }: { pangolin: Pangolin }) => {
  const styles = useStyles(themedStyles);
  const moduleIcons = useStyles(themedModuleIconClassStyles);
  return (
    <div className={styles.linksContainer}>
      <Link path={{ kind: BasePath.HISTORY_LIST, pangolinID: pangolin.id }} className={styles.link}>
        <div className={styles.linksRow}>
          <div>
            <div className={classNames(styles.icon, moduleIcons.history)}/>
          </div>
          <span className={styles.linkLabel}>{Messages.trafficHistory()}</span>
        </div>
      </Link>
      <Link path={{ kind: BasePath.BANDWIDTH_LIST, pangolinID: pangolin.id }} className={styles.link}>
        <div className={styles.linksRow}>
          <div>
            <div className={classNames(styles.icon, moduleIcons.bandwidth)}/>
          </div>
          <span className={styles.linkLabel}>{Messages.bandwidthPrioritization()}</span>
        </div>
      </Link>
      <Link path={{ kind: BasePath.SCHEDULE_LIST, pangolinID: pangolin.id }} className={styles.link}>
        <div className={styles.linksRow}>
          <div>
            <div className={classNames(styles.icon, moduleIcons.schedules)}/>
          </div>
          <span className={styles.linkLabel}>{Messages.parentalControls()}</span>
        </div>
      </Link>
      <Link path={{ kind: BasePath.VPN_LIST, pangolinID: pangolin.id }} className={styles.link}>
        <div className={styles.linksRow}>
          <div>
            <div className={classNames(styles.icon, moduleIcons.vpnserver)}/>
          </div>
          <span className={styles.linkLabel}>{Messages.vpnServer()}</span>
        </div>
      </Link>
    </div>
  );
};

type BaseDeviceInformationProps = {
  user: User;
  pangolin: Pangolin;
  domain: IPangolinDomain;
};

export const BaseDeviceInformation = ({ user, pangolin, domain }: BaseDeviceInformationProps) => {
  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <div className={styles.row}>
        <BackButton/>
      </div>
      <div className={styles.deviceNameContainer}>
        {pangolin.nickname}
      </div>
      <div className={styles.row}>
        <PrimaryInfo pangolin={pangolin}/>
        <SecondaryInfo user={user} domain={domain}/>
      </div>
      <div className={styles.throughputContainer}>
        <PangolinNetworkThroughput pangolin={domain}/>
      </div>
      <Links pangolin={pangolin}/>
    </StandardPage>
  );
};

export const DeviceInformation = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const user = accounts.user;
  if (!user) return <Redirect />;

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseDeviceInformation
      user={user}
      pangolin={pangolin}
      domain={domain}
    />
  );
});
